// @flow
import {takeEvery, put, call, select} from 'redux-saga/effects';
import {stopSubmit} from 'redux-form';
import {tripica} from '../../../api';
import {setSIMCardNumber} from './actions';
import type {SignUpSIMCardScanActionType} from './actions';
import {setLoading} from '../../LoadingStatus';
import {setOrderUuid} from '../Order';
import I18n from '../../../lib/i18n';
import HandleErrorService from '../../../lib/handleError';
import {
  goToNextActivationPage,
  selectSubscriptionForActivation,
} from '../sagas';
import {initNewOfferWithSelectedSubscription} from '../../NewOffer/sagas';
import {getDeviceUniqueId} from '../../../lib/device';
import {isLoggedInSelector} from '../../Authentication';
import WizzeeLog from '../../../lib/wizzeeLog';

export function* confirmSimCardNumber(
  action: SignUpSIMCardScanActionType,
): Generator<*, *, *> {
  if (action.type !== 'SIGNUP_CONFIRM_SIM_CARD_NUMBER') {
    return;
  } // for flow
  yield put(setLoading('confirmSimCardNumber', true));
  try {
    const isLoggedIn = yield select(isLoggedInSelector);
    const catalog = yield call([tripica, 'getCatalog']);
    const deviceUniqueId = yield call(getDeviceUniqueId);
    const response = yield call(
      [tripica, 'validateSim'],
      action.payload.number,
      catalog,
      deviceUniqueId,
      undefined,
      {bypassUnauthorizedRequestCheck: !isLoggedIn}, // prevent 401 error if user is not logged in, as SIM validation is possible without authentication
    );
    if (response.errorCodeList.includes('SIM_NOT_FOUND')) {
      yield put(
        stopSubmit('SIMCard', {
          number: I18n.t('SignUp.SIMCardManual.numberDoesNotExist'),
        }),
      );
    } else if (response.errorCodeList.includes('SIM_NOT_AVAILABLE')) {
      yield put(
        stopSubmit('SIMCard', {
          number: I18n.t('SignUp.SIMCardManual.alreadyActivated'),
        }),
      );
    } else {
      yield put(setSIMCardNumber(action.payload.number));
      yield put(setOrderUuid(response.uuid));

      yield call(selectSubscriptionForActivation);
      yield call(initNewOfferWithSelectedSubscription);
      yield call(goToNextActivationPage, false, true);
    }
  } catch (e) {
    WizzeeLog.warn('[saga] confirmSimCardNumber', e);
    if (HandleErrorService.isNoNetworkError(e)) {
      HandleErrorService.showToastError(e);
    } else {
      yield put(
        stopSubmit('SIMCard', {number: HandleErrorService.getErrorMessage(e)}),
      );
    }
  } finally {
    yield put(setLoading('confirmSimCardNumber', false));
  }
}

export default function* (): Generator<*, *, *> {
  yield* [takeEvery('SIGNUP_CONFIRM_SIM_CARD_NUMBER', confirmSimCardNumber)];
}
